import { Link as RemixLink } from "@remix-run/react";
import {
  Link,
  UnorderedList,
  ListItem as ListItemChakra,
  Text,
} from "@chakra-ui/react";

import { Definition } from "#app/components/index.jsx";
import { RESERVATION_SYSTEM_COUNT } from "#app/config/countries.js";
import { plans } from "#app/config/stripe.js";

import { List, ListItem, ListItemHeader, ListItemContent } from "./FaqList.jsx";

const TRIAL_PRICING_ANSWER = `Every new account starts with ${plans.trial.trialNotifications} free email notifications and can watch ${plans.trial.maxActiveRequests} active request so you can try it out, no credit card required.`;
const PAID_PRICING_ANSWER = `Paid plans unlock unlimited notifications and raise that limit to ${plans.basic.maxActiveRequests} active requests on Basic or ${plans.pro.maxActiveRequests} on Priority, plus text messages, WhatsApp, push notifications, and faster checks.`;

export const PRICING_FAQ_ANSWER = `${TRIAL_PRICING_ANSWER} ${PAID_PRICING_ANSWER}`;

export function FAQ() {
  return (
    <List>
      <ListItem>
        <ListItemHeader title="What is Schnerp?" />
        <ListItemContent>
          <Text mb={4}>
            We help you find available sites at sold out campgrounds.
          </Text>
          <Text mb={4}>
            With parks opening for reservations months in advance, and popular
            campgrounds selling out within minutes on opening day, we discovered
            that the best way to get a site is when someone’s plans change and
            they cancel their reservation.
          </Text>
          <Text>
            To find a cancellation, you could monitor the campground's website
            and refresh the page over and over again, or you could tell us what
            campground you're looking for and let us do the hard work.
          </Text>
        </ListItemContent>
      </ListItem>

      <ListItem>
        <ListItemHeader title="How does it work?" />
        <ListItemContent>
          <Text mb={4}>
            First, you{" "}
            <Link as={RemixLink} to="/new">
              create a request
            </Link>
            , telling us what campground you're watching, your arrival dates,
            and your preferred campsites.
          </Text>
          <Text mb={4}>
            Then, our system takes over and checks your selected campground for
            a cancellation. If one of the sites in your availability window
            becomes available, we send you an email (or a text) with a link to
            book the spot.
          </Text>
          <Text>
            Please keep in mind, finding an opening is never guaranteed. If
            there's no availability today, we have to rely on someone else
            cancelling their existing reservation, and sometimes that just
            doesn't happen.
          </Text>
        </ListItemContent>
      </ListItem>

      <ListItem>
        <ListItemHeader
          title={
            <>
              {" "}
              So why{" "}
              <Text as="span" fontStyle="italic">
                "Schnerp"
              </Text>
              ?
            </>
          }
        />
        <ListItemContent>
          <Definition mb={4} />
          <Text mb={4}>
            It's a word my friend group uses when talking about hunting powder
            on a ski resort a few days after a storm. We'll put in massive
            efforts, staying high on traverses and suffering long run-outs, just
            to{" "}
            <Text as="span" fontStyle="italic">
              schnerp
            </Text>{" "}
            a single turn in untracked snow.
          </Text>
          <Text mb={4}>
            In that context, it vaguely means to acquire a scarce resource. When
            I was thinking about names for this project, I realized we're doing
            the same here, just with campsites.
          </Text>
          <Text>Also, it's really fun to say.</Text>
        </ListItemContent>
      </ListItem>

      <ListItem>
        <ListItemHeader title="Do you book a site for me?" />
        <ListItemContent>
          <Text>
            No. We only monitor the availability of sites in campgrounds and
            notify you of an opening. It's up to you to research which one is
            right for you, and make the booking via the park's reservations
            system.
          </Text>
        </ListItemContent>
      </ListItem>

      <ListItem>
        <ListItemHeader title="What campgrounds can I watch?" />
        <ListItemContent>
          <Text mb={4}>
            We watch campgrounds across {RESERVATION_SYSTEM_COUNT} booking
            systems in Canada, the United States, Australia, and New Zealand —
            everything from national and state parks to holiday parks and
            backcountry huts.
          </Text>
          <Text mb={4}>
            See{" "}
            <Link as={RemixLink} to="/new">
              the full list of booking systems we watch
            </Link>{" "}
            on the Create a New Request page, organized by country.
          </Text>
          <Text>
            We're always expanding to new parks and booking systems. If there's
            a park you'd like us to add, let us know at{" "}
            <Link
              isExternal
              href="mailto:help@schnerp.com?subject=Park%20suggestion"
            >
              help@schnerp.com
            </Link>
            .
          </Text>
        </ListItemContent>
      </ListItem>

      <ListItem>
        <ListItemHeader title="How much does it cost?" />
        <ListItemContent>
          <Text mb={4}>{TRIAL_PRICING_ANSWER}</Text>
          <Text mb={4}>{PAID_PRICING_ANSWER}</Text>
          <Text>
            <Link as={RemixLink} to="/pricing">
              Read more about our pricing.
            </Link>
          </Text>
        </ListItemContent>
      </ListItem>

      <ListItem>
        <ListItemHeader title="How often do you check a site?" />
        <ListItemContent>
          <Text>
            We check most parks every 1-5 minutes, but it's not a fixed rate.
            The more popular a park is, the faster we check it.
          </Text>
        </ListItemContent>
      </ListItem>

      <ListItem>
        <ListItemHeader title="How can I watch for multiple sites?" />
        <ListItemContent>
          <Text mb={4}>
            When creating a request, look for the{" "}
            <Text as="strong">Double Site</Text> or{" "}
            <Text as="strong">Party Size</Text> filters if you need adjacent or
            group sites.
          </Text>
          <Text>
            You can also create multiple requests to watch different campgrounds
            or date ranges at the same time.
          </Text>
        </ListItemContent>
      </ListItem>

      <ListItem>
        <ListItemHeader title="Why am I not seeing some campsites listed?" />
        <ListItemContent>
          <Text mb={4}>
            For the campgrounds that we do support, we run an automated process
            to collect a campsite list. This process isn't perfect and
            occasionally, we miss some.
          </Text>
          <Text mb={4}>
            Backcountry sites can sometimes be tricky to find, hidden behind
            another name or in a different section of the campground page.
          </Text>
          <Text>
            If you find a mistake in the site listing, email us at{" "}
            <Link
              isExternal
              href="mailto:help@schnerp.com?subject=Schnerp%20Site%20Feedback"
            >
              help@schnerp.com
            </Link>
          </Text>
        </ListItemContent>
      </ListItem>

      <ListItem>
        <ListItemHeader title="Don't some parks already offer notifications?" />
        <ListItemContent>
          <Text mb={4}>
            A few agencies like Parks Canada and BC Parks have added basic
            notification features. Schnerp goes further:
          </Text>
          <UnorderedList mb={4}>
            <ListItemChakra>
              <strong>More notification channels:</strong> Email, text,
              WhatsApp, and push notifications.
            </ListItemChakra>
            <ListItemChakra>
              <strong>More site types:</strong> Backcountry, day use, lodging,
              and huts.
            </ListItemChakra>
            <ListItemChakra>
              <strong>Flexible dates:</strong> Watch a range of dates instead of
              a single night.
            </ListItemChakra>
            <ListItemChakra>
              <strong>Refined filters:</strong> Electric-only RV sites,
              waterfront, double sites, and more.
            </ListItemChakra>
            <ListItemChakra>
              <strong>Works everywhere:</strong> One account across dozens of
              booking systems in four countries.
            </ListItemChakra>
          </UnorderedList>
        </ListItemContent>
      </ListItem>

      <ListItem>
        <ListItemHeader title="Does your service slow down park websites?" />
        <ListItemContent>
          <Text mb={4}>
            No, we feel very strongly about not abusing park websites. Our
            service is designed to combine checks from multiple requests into a
            single query, which is more efficient than if every person manually
            refreshed the page themselves.
          </Text>
          <Text>
            Much like when we're out in nature, we do our best to leave no
            trace.
          </Text>
        </ListItemContent>
      </ListItem>
    </List>
  );
}
